import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CalendarDays, Check, ChevronDown, ChevronRight, GripVertical, MoreHorizontal, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MarkdownDocument } from "@/components/life-strategy";
import { localDateKey } from "../../shared/planning.js";

const previewTransform = (x, y, width) => `translate3d(${Math.max(8, Math.min(x + 16, window.innerWidth - width - 8))}px, ${Math.max(8, Math.min(y + 16, window.innerHeight - 64))}px, 0)`;
const formatDate = (value) => new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(new Date(`${value}T12:00:00`));
function CardTitle({ text }) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`|~~[^~]+~~|#[\p{L}\p{N}_/-]+)/gu).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`")) return <code key={index}>{part.slice(1, -1)}</code>;
    if (part.startsWith("~~") && part.endsWith("~~")) return <s key={index}>{part.slice(2, -2)}</s>;
    if (part.startsWith("#")) return <span key={index} className="kanban-tag">{part}</span>;
    return part;
  });
}

function CardComposer({ initial, onSave, onCancel, busy, compact = false }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [error, setError] = useState("");
  const input = useRef(null);
  const submitting = useRef(false);
  useEffect(() => { input.current?.focus({ preventScroll: true }); }, []);
  const submit = async (event) => {
    event?.preventDefault();
    if (!title.trim() || busy || submitting.current) return;
    submitting.current = true;
    setError("");
    try { await onSave({ title: title.trim() }); setTitle(""); input.current?.focus({ preventScroll: true }); }
    catch (cause) { setError(cause.message); }
    finally { submitting.current = false; }
  };
  return <form className="kanban-composer" onSubmit={submit} onKeyDown={(event) => {
    if (event.key === "Escape" && !busy) { event.stopPropagation(); onCancel(); }
  }}>
    <Textarea ref={input} aria-label={initial ? "Текст карточки" : "Новая карточка"} placeholder="Текст карточки…" rows={2} maxLength={200} value={title} disabled={busy} onChange={(event) => setTitle(event.target.value)} onKeyDown={(event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void submit(); }
    }} />
    {error && <p className="kanban-error" role="alert">{error}</p>}
    <div className="flex items-center gap-2"><Button type="submit" className="min-w-0 flex-1" disabled={busy || !title.trim()}>{initial ? "Сохранить" : "Добавить"}</Button><Button variant="ghost" size="icon" type="button" disabled={busy} onClick={onCancel} aria-label="Отменить ввод"><X /></Button></div>
    {compact && <span className="kanban-caption">Enter — добавить · Shift+Enter — новая строка</span>}
  </form>;
}

function ColumnComposer({ initial = "", onSave, onCancel, busy }) {
  const [title, setTitle] = useState(initial);
  const [error, setError] = useState("");
  const input = useRef(null);
  const submitting = useRef(false);
  useEffect(() => { input.current?.focus({ preventScroll: true }); input.current?.select(); }, []);
  return <form className="kanban-composer" onSubmit={async (event) => {
    event.preventDefault();
    if (!title.trim() || busy || submitting.current) return;
    submitting.current = true;
    try { await onSave(title.trim()); } catch (cause) { setError(cause.message); } finally { submitting.current = false; }
  }} onKeyDown={(event) => { if (event.key === "Escape" && !busy) onCancel(); }}>
    <Input ref={input} aria-label="Название списка" placeholder="Название списка" value={title} maxLength={100} disabled={busy} onChange={(event) => setTitle(event.target.value)} />
    {error && <p className="kanban-error" role="alert">{error}</p>}
    <div className="flex gap-2"><Button type="submit" className="min-w-0 flex-1" disabled={busy || !title.trim()}>{initial ? "Сохранить" : "Добавить список"}</Button><Button type="button" size="icon" variant="ghost" aria-label="Отменить список" disabled={busy} onClick={onCancel}><X /></Button></div>
  </form>;
}

export function PlanningKanban({ api, tasks, columns, onTasksChange, onColumnsChange, onSave, onEdit }) {
  const mobile = useIsMobile();
  const [addingColumn, setAddingColumn] = useState(false);
  const [addingCard, setAddingCard] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [drag, setDrag] = useState(null);
  const [drop, setDrop] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const boardRef = useRef(null);
  const dragSession = useRef(null);
  const dragFrame = useRef(null);
  const dragPreviewRef = useRef(null);
  const pointer = useRef(null);
  const pointerCleanup = useRef(null);
  const suppressClick = useRef(false);
  useEffect(() => () => {
    pointerCleanup.current?.();
    cancelAnimationFrame(dragFrame.current);
    dragSession.current = null;
  }, []);
  const run = async (operation) => {
    if (busyRef.current) throw new Error("Дождитесь сохранения предыдущего изменения");
    busyRef.current = true; setBusy(true);
    try { return await operation(); } finally { busyRef.current = false; setBusy(false); }
  };
  const act = (operation) => void run(operation).catch((error) => toast.error(error.message));
  const patchColumn = async (id, patch) => {
    const result = await api.patch(`columns/${id}`, patch);
    onColumnsChange(result.columns);
  };
  const moveColumn = async (id, beforeId) => {
    const result = await api.patch(`columns/${id}/move`, { beforeId });
    onColumnsChange(result.columns); setAnnouncement("Порядок списков сохранён");
  };
  const moveTask = async (id, columnId, beforeId = null) => {
    const result = await api.patch(`tasks/${id}/move`, { columnId, beforeId });
    onTasksChange(result.tasks); onColumnsChange(result.columns); setAnnouncement("Положение карточки сохранено");
  };
  const visibleTasks = tasks.filter((task) => !task.archived);
  const stopDrag = () => {
    cancelAnimationFrame(dragFrame.current);
    dragFrame.current = null;
    dragSession.current = null;
    setDrag(null); setDrop(null);
  };
  const targetAt = (x, y, source) => {
    const board = boardRef.current;
    const element = document.elementFromPoint(x, y);
    if (!source || !element || !board?.contains(element)) return null;
    if (source.kind === "column") {
      // Columns have different heights. Their horizontal insertion positions
      // cover the whole board, including gaps, empty space and the trailing rail.
      const lists = [...board.querySelectorAll("[data-kanban-column]")]
        .filter((list) => list.dataset.kanbanColumn !== source.id);
      const next = lists.find((list) => {
        const bounds = list.getBoundingClientRect();
        return mobile ? y < bounds.top + bounds.height / 2 : x < bounds.left + bounds.width / 2;
      });
      const beforeId = next?.dataset.kanbanColumn || null;
      const index = columns.findIndex((column) => column.id === source.id);
      if (index < 0 || beforeId === (columns[index + 1]?.id || null)) return null;
      return { beforeId };
    }
    const list = element.closest("[data-kanban-column]");
    if (!list) return null;
    const columnId = list.dataset.kanbanColumn;
    const card = element.closest("[data-kanban-card]");
    let beforeId = card?.dataset.kanbanCard || null;
    if (card && y > card.getBoundingClientRect().top + card.getBoundingClientRect().height / 2) {
      const siblings = tasks.filter((task) => task.columnId === columnId && !task.archived);
      beforeId = siblings[siblings.findIndex((task) => task.id === beforeId) + 1]?.id || null;
    }
    return { columnId, beforeId };
  };
  const updateDrag = (x, y) => {
    const session = dragSession.current;
    if (!session) return;
    session.point = { x, y };
    const preview = dragPreviewRef.current;
    if (preview) preview.style.transform = previewTransform(x, y, preview.offsetWidth);
    session.target = targetAt(x, y, session.source);
    setDrop((previous) => previous?.beforeId === session.target?.beforeId && previous?.columnId === session.target?.columnId ? previous : session.target);
  };
  const startDrag = (source, x, y) => {
    stopDrag();
    const session = { source, point: { x, y }, target: null };
    dragSession.current = session;
    const width = source.kind === "column" ? Math.min(boardRef.current.querySelector("[data-kanban-column]").offsetWidth, window.innerWidth - 16) : 0;
    setDrag({ ...source, preview: width ? { width, x, y } : null });
    let previousTime;
    const frame = (time) => {
      if (dragSession.current !== session) return;
      const elapsed = Math.min(time - (previousTime ?? time), 32);
      previousTime = time;
      const board = boardRef.current;
      const point = session.point;
      if (board && point && board.contains(document.elementFromPoint(point.x, point.y))) {
        const bounds = board.getBoundingClientRect();
        const speed = (position, start, end) => position < start + 48
          ? -Math.min(1, (start + 48 - position) / 48)
          : position > end - 48 ? Math.min(1, (position - end + 48) / 48) : 0;
        const left = board.scrollLeft, top = board.scrollTop;
        // Keep scrolling even when the cursor is held still at an edge. Resolve
        // the insertion position again after scrolling moves columns underneath.
        if (!mobile) board.scrollLeft += speed(point.x, bounds.left, bounds.right) * elapsed * 0.9;
        board.scrollTop += speed(point.y, bounds.top, bounds.bottom) * elapsed * 0.9;
        if (left !== board.scrollLeft || top !== board.scrollTop) updateDrag(point.x, point.y);
      }
      dragFrame.current = requestAnimationFrame(frame);
    };
    dragFrame.current = requestAnimationFrame(frame);
    updateDrag(x, y);
  };
  const finishDrop = (source, target) => {
    stopDrag();
    if (!source || !target || busyRef.current) return;
    if (source.kind === "column") {
      const index = columns.findIndex((column) => column.id === source.id);
      if (index >= 0 && source.id !== target.beforeId && target.beforeId !== (columns[index + 1]?.id || null)) act(() => moveColumn(source.id, target.beforeId));
    } else if (source.id !== target.beforeId) act(() => moveTask(source.id, target.columnId, target.beforeId));
  };
  const nativeDrag = (event, kind, id) => {
    if (busyRef.current || pointer.current) { event.preventDefault(); return; }
    event.stopPropagation();
    event.dataTransfer.setData("application/x-rollapp-kanban", JSON.stringify({ kind, id }));
    event.dataTransfer.effectAllowed = "move";
    startDrag({ kind, id }, event.clientX, event.clientY);
  };
  // A dedicated handle keeps normal touch scrolling available on the rest of a card.
  const pointerDrag = (event, kind, id) => {
    if ((kind === "task" && event.pointerType === "mouse") || !event.isPrimary || event.button !== 0 || busyRef.current) return;
    event.preventDefault();
    pointerCleanup.current?.();
    const source = { kind, id };
    const state = { id: event.pointerId, x: event.clientX, y: event.clientY, active: false };
    pointer.current = state;
    const capture = event.currentTarget;
    capture.setPointerCapture(event.pointerId);
    const move = (next) => {
      if (next.pointerId !== state.id) return;
      if (!state.active && Math.hypot(next.clientX - state.x, next.clientY - state.y) < 8) return;
      if (!state.active) { state.active = true; startDrag(source, next.clientX, next.clientY); }
      suppressClick.current = true;
      updateDrag(next.clientX, next.clientY);
    };
    const clean = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", cancel); window.removeEventListener("keydown", keydown); window.removeEventListener("blur", cancel);
      capture.removeEventListener("lostpointercapture", cancel); document.removeEventListener("visibilitychange", visibility);
      if (capture.hasPointerCapture(state.id)) capture.releasePointerCapture(state.id);
      pointer.current = null; pointerCleanup.current = null;
      setTimeout(() => { suppressClick.current = false; }, 0);
    };
    const up = (next) => {
      if (next.pointerId !== state.id) return;
      const target = state.active ? targetAt(next.clientX, next.clientY, source) : null;
      clean();
      if (state.active) finishDrop(source, target);
    };
    const cancel = (next) => { if (next?.pointerId != null && next.pointerId !== state.id) return; clean(); stopDrag(); };
    const keydown = (next) => { if (next.key === "Escape") cancel(); };
    const visibility = () => { if (document.hidden) cancel(); };
    pointerCleanup.current = cancel;
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", cancel); window.addEventListener("keydown", keydown); window.addEventListener("blur", cancel);
    capture.addEventListener("lostpointercapture", cancel); document.addEventListener("visibilitychange", visibility);
  };
  useEffect(() => {
    pointerCleanup.current?.();
    stopDrag();
  }, [mobile, columns, tasks]);
  const cardView = (task) => {
    if (editingCard === task.id) return <div key={task.id} className="kanban-card"><CardComposer initial={task} busy={busy} onCancel={() => setEditingCard(null)} onSave={(patch) => run(async () => { await onSave(task.id, patch, { quiet: true }); setEditingCard(null); })} /></div>;
    const siblings = tasks.filter((item) => item.columnId === task.columnId && !item.archived);
    const index = siblings.findIndex((item) => item.id === task.id);
    return <article key={task.id} className="kanban-card" data-kanban-card={task.id} data-completed={task.completed || undefined} data-dragging={drag?.id === task.id || undefined} data-drop-before={drag?.kind === "task" && drop?.beforeId === task.id || undefined} draggable={!mobile && !busy} onDragStart={(event) => nativeDrag(event, "task", task.id)} onDragEnd={stopDrag}>
      <div className="kanban-card__top">
        <Checkbox aria-label={`Выполнено: ${task.title}`} checked={task.completed} disabled={busy} onCheckedChange={(checked) => act(() => onSave(task.id, { completed: checked === true }, { quiet: true }))} />
        <Button variant="ghost" className="kanban-card__title" onClick={() => { if (!suppressClick.current) setEditingCard(task.id); }} disabled={busy}><CardTitle text={task.title} /></Button>
        <DropdownMenu><DropdownMenuTrigger render={<Button variant="ghost" size="icon" className="kanban-card__menu" disabled={busy} />} aria-label={`Меню карточки ${task.title}`}><MoreHorizontal /></DropdownMenuTrigger>
          <DropdownMenuContent className="rollapp-body min-w-56" align="end">
            <DropdownMenuItem onClick={() => setEditingCard(task.id)}><Pencil />Изменить текст</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(task)}><CalendarDays />Дата и подробности</DropdownMenuItem>
            <DropdownMenuSub><DropdownMenuSubTrigger><ArrowRight />Переместить в список</DropdownMenuSubTrigger><DropdownMenuSubContent className="rollapp-body min-w-56">{columns.map((column) => <DropdownMenuItem key={column.id} disabled={column.id === task.columnId} onClick={() => act(() => moveTask(task.id, column.id))}>{column.title}</DropdownMenuItem>)}</DropdownMenuSubContent></DropdownMenuSub>
              <DropdownMenuItem disabled={index === 0} onClick={() => act(() => moveTask(task.id, task.columnId, siblings[index - 1].id))}><ArrowUp />Выше</DropdownMenuItem>
              <DropdownMenuItem disabled={index === siblings.length - 1} onClick={() => act(() => moveTask(task.id, task.columnId, siblings[index + 2]?.id || null))}><ArrowDown />Ниже</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {task.description && <MarkdownDocument className="kanban-card__markdown" label={`Описание: ${task.title}`} source={task.description} taskDisabled={busy} onTaskCheckedChange={(line, checked) => {
        const lines = task.description.split("\n"); lines[line] = lines[line].replace(/^- \[[ xX]\]/, `- [${checked ? "x" : " "}]`);
        act(() => onSave(task.id, { description: lines.join("\n") }, { quiet: true }));
      }} />}
      <div className="kanban-card__footer" data-has-date={Boolean(task.dueOn) || undefined}>
        {task.dueOn && <span className="kanban-card__date" data-overdue={!task.completed && task.dueOn < localDateKey(new Date()) || undefined}><CalendarDays className="size-4" />{formatDate(task.dueOn)}</span>}
        <Button variant="ghost" size="icon" className="kanban-drag-handle" aria-label={`Перетащить карточку ${task.title}`} title="Перетащите карточку или используйте её меню" disabled={busy} draggable onDragStart={(event) => nativeDrag(event, "task", task.id)} onPointerDown={(event) => pointerDrag(event, "task", task.id)}><GripVertical /></Button>
      </div>
    </article>;
  };
  return <div className="kanban-workspace" aria-busy={busy}>
    <span role="status" className="sr-only">{announcement}</span>
    {drag?.kind === "column" && drag.preview && <div ref={dragPreviewRef} className="kanban-column-drag-preview" aria-hidden="true" style={{ width: drag.preview.width, transform: previewTransform(drag.preview.x, drag.preview.y, drag.preview.width) }}><GripVertical /><span>{columns.find((column) => column.id === drag.id)?.title}</span></div>}
    <div ref={boardRef} className="planning-board" aria-label="Kanban-доска" onDragOver={(event) => {
      if (!dragSession.current || !event.dataTransfer.types.includes("application/x-rollapp-kanban")) return;
      event.preventDefault(); event.dataTransfer.dropEffect = "move";
      updateDrag(event.clientX, event.clientY);
    }} onDrop={(event) => {
      const source = dragSession.current?.source;
      if (!source || !event.dataTransfer.types.includes("application/x-rollapp-kanban")) return;
      event.preventDefault();
      finishDrop(source, targetAt(event.clientX, event.clientY, source));
    }} onDragLeave={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        if (dragSession.current) dragSession.current.point = null;
        setDrop(null);
      }
    }}>
      {columns.map((column, columnIndex) => {
        const columnTasks = visibleTasks.filter((task) => task.columnId === column.id);
        const total = tasks.filter((task) => task.columnId === column.id && !task.archived).length;
        return <section className="planning-column" key={column.id} data-kanban-column={column.id} data-status={column.id} data-collapsed={column.collapsed || undefined} data-dragging={drag?.kind === "column" && drag.id === column.id || undefined} data-drop-column={drag?.kind === "column" && drop?.beforeId === column.id || undefined} aria-label={column.title}>
          {renaming === column.id ? <ColumnComposer initial={column.title} busy={busy} onCancel={() => setRenaming(null)} onSave={(title) => run(async () => { await patchColumn(column.id, { title }); setRenaming(null); })} /> : <header className="planning-column__header">
            <Button variant="ghost" size="icon" className="kanban-drag-handle" aria-label={`Перетащить список ${column.title}`} disabled={busy} draggable={false} onPointerDown={(event) => pointerDrag(event, "column", column.id)}><GripVertical /></Button>
            <Button variant="ghost" className="planning-column__title" onClick={() => act(() => patchColumn(column.id, { collapsed: !column.collapsed }))} disabled={busy} aria-expanded={!column.collapsed}>{column.collapsed ? <ChevronRight /> : <ChevronDown />}<span>{column.title}</span>{column.complete && <Check className="size-4" />}</Button><Badge variant="secondary">{total}</Badge>
            <DropdownMenu><DropdownMenuTrigger render={<Button variant="ghost" size="icon" disabled={busy} />} aria-label={`Меню списка ${column.title}`}><MoreHorizontal /></DropdownMenuTrigger><DropdownMenuContent className="rollapp-body min-w-56" align="end">
              <DropdownMenuItem onClick={() => setRenaming(column.id)}><Pencil />Переименовать</DropdownMenuItem>
              <DropdownMenuItem onClick={() => act(() => patchColumn(column.id, { complete: !column.complete }))}><Check />{column.complete ? "Не завершать новые карточки" : "Завершать новые карточки"}</DropdownMenuItem>
              <DropdownMenuItem disabled={columnIndex === 0} onClick={() => act(() => moveColumn(column.id, columns[columnIndex - 1].id))}>{mobile ? <ArrowUp /> : <ArrowLeft />}{mobile ? "Сдвинуть выше" : "Сдвинуть влево"}</DropdownMenuItem>
              <DropdownMenuItem disabled={columnIndex === columns.length - 1} onClick={() => act(() => moveColumn(column.id, columns[columnIndex + 2]?.id || null))}>{mobile ? <ArrowDown /> : <ArrowRight />}{mobile ? "Сдвинуть ниже" : "Сдвинуть вправо"}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" className="app-destructive-menu-item" disabled={columns.length === 1 || tasks.some((task) => task.columnId === column.id)} onClick={() => act(async () => { const result = await api.delete(`columns/${column.id}`); onColumnsChange(result.columns); })}><Trash2 />Удалить пустой список</DropdownMenuItem>
            </DropdownMenuContent></DropdownMenu>
          </header>}
          {!column.collapsed && <>
            <div className="planning-column__tasks" data-drop-end={drag?.kind === "task" && drop?.columnId === column.id && !drop.beforeId || undefined}>
              {columnTasks.map(cardView)}
              {!columnTasks.length && <p className="kanban-empty">Нет карточек</p>}
            </div>
            {addingCard === column.id ? <CardComposer compact busy={busy} onCancel={() => setAddingCard(null)} onSave={(patch) => run(() => onSave(null, { ...patch, columnId: column.id }, { quiet: true }))} /> : <Button variant="ghost" className="planning-column__add" disabled={busy} onClick={() => setAddingCard(column.id)}><Plus />Добавить карточку</Button>}
          </>}
        </section>;
      })}
      <div className="kanban-new-column" data-drop-column-end={drag?.kind === "column" && drop && drop.beforeId === null || undefined}>{addingColumn ? <ColumnComposer busy={busy} onCancel={() => setAddingColumn(false)} onSave={(title) => run(async () => { const result = await api.post("columns", { title }); onColumnsChange(result.columns); setAddingColumn(false); })} /> : <Button variant="ghost" className="kanban-new-column__button" disabled={busy} onClick={() => setAddingColumn(true)}><Plus />Добавить список</Button>}</div>
    </div>
  </div>;
}
